<template>
    <Head :title="`Triage: ${incident.incident_number}`" />

    <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <!-- Header -->
        <div class="flex items-center justify-between mb-6">
            <div class="flex items-center space-x-4">
                <Link
                    :href="route('incidents.show', incident.id)"
                    class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                    <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
                    </svg>
                </Link>
                <div class="flex items-center gap-3">
                    <div>
                        <h2 class="text-2xl font-semibold text-gray-900 dark:text-white">
                            Triage Incident
                        </h2>
                        <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
                            Review and decide on the appropriate response path
                        </p>
                    </div>
                    <InfoButton title="Incident Triage Process" variant="default">
                        <p>
                            <strong class="text-secondary-900 dark:text-white">About this page</strong><br>
                            The Incident Triage page is where managers assess reported incidents and near misses to
                            determine the appropriate response path. Triage is a critical decision point that ensures
                            organizational resources are allocated appropriately based on incident severity, potential
                            impact, and complexity. This process establishes the foundation for effective incident
                            resolution by setting severity levels, priority, and determining whether the incident
                            requires no action, a simple quick fix, or a comprehensive investigation with root cause
                            analysis.
                        </p>
                        <p>
                            <strong class="text-secondary-900 dark:text-white">How to use</strong><br>
                            • <strong>Review Incident Summary:</strong> Examine the incident details including title,
                              description, type, location, reporter, and reported date<br>
                            • <strong>SLA Notice:</strong> If displayed, note the required response time for this
                              incident type to ensure timely action<br>
                            • <strong>Select Decision Path:</strong> Choose from four triage decisions based on your
                              assessment:<br>
                            &nbsp;&nbsp;&nbsp;- <strong>No Action:</strong> Close incident immediately (duplicate, no action
                              needed, insufficient info, out of scope, or already resolved)<br>
                            &nbsp;&nbsp;&nbsp;- <strong>Quick Fix:</strong> Assign a simple corrective action to a team
                              member with clear instructions and due date<br>
                            &nbsp;&nbsp;&nbsp;- <strong>Investigation:</strong> Initiate formal investigation to identify
                              root causes, analyze contributing factors, and develop comprehensive corrective actions<br>
                            • <strong>Assess Severity:</strong> Rate the actual or potential impact - Low (minor), Medium
                              (moderate), High (significant), or Critical (severe)<br>
                            • <strong>Set Priority:</strong> Determine urgency - Low (normal workflow), Normal (standard
                              response), High (prompt attention), or Urgent (immediate action)<br>
                            • <strong>Manager Assessment:</strong> Document your reasoning for the chosen path, including
                              factors considered and decision rationale<br>
                            • <strong>Complete Required Fields:</strong> Based on your decision, provide additional
                              details:<br>
                            &nbsp;&nbsp;&nbsp;- <strong>No Action:</strong> Select closure reason and provide detailed
                              explanation<br>
                            &nbsp;&nbsp;&nbsp;- <strong>Quick Fix:</strong> Define action title, description, assign to
                              team member, set due date<br>
                            &nbsp;&nbsp;&nbsp;- <strong>Investigation:</strong> Describe investigation scope and assign
                              to investigator<br>
                            • <strong>Submit Decision:</strong> Click the appropriate button (Close Incident, Assign
                              Quick Fix, or Start Investigation)
                        </p>
                        <p>
                            <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
                            • <strong>Act promptly</strong> - Timely triage ensures SLA compliance and demonstrates
                              organizational responsiveness<br>
                            • <strong>Consider severity vs. priority</strong> - Severity measures impact, priority
                              determines response urgency. A low severity incident might still be high priority if it
                              affects critical operations<br>
                            • <strong>Choose Quick Fix wisely</strong> - Use this path when the solution is clear, simple,
                              and can be completed quickly without deep analysis<br>
                            • <strong>Investigate when uncertain</strong> - If root causes are unclear or the incident
                              could indicate systemic issues, choose Investigation<br>
                            • <strong>Near misses deserve attention</strong> - Don't automatically choose "No Action"
                              for near misses; they're valuable learning opportunities<br>
                            • <strong>Document thoroughly</strong> - Your assessment creates an audit trail and helps
                              future analysis of incident trends<br>
                            • <strong>Assignment matters</strong> - Select team members with appropriate skills and
                              availability for quick fixes and investigations<br>
                            • <strong>Set realistic due dates</strong> - Consider team capacity and incident complexity
                              when establishing deadlines<br>
                            • <strong>SLA-driven incidents</strong> require extra attention to response timelines<br>
                            • Your triage decision determines the entire workflow path - take time to assess carefully
                        </p>
                    </InfoButton>
                </div>
            </div>
        </div>

        <!-- Incident Summary Card -->
        <div class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow p-6 mb-6">
            <div class="flex items-start justify-between mb-4">
                <div>
                    <div class="flex items-center space-x-3 mb-2">
                        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">{{ incident.incident_number }}</h3>
                        <span v-if="incident.is_near_miss" class="px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
                            Near Miss
                        </span>
                        <span v-else class="px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
                            Incident
                        </span>
                    </div>
                    <h4 class="text-base font-medium text-gray-900 dark:text-white mb-2">{{ incident.title }}</h4>
                    <p class="text-sm text-gray-600 dark:text-gray-400">{{ incident.description }}</p>
                </div>
            </div>

            <div class="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div>
                    <p class="text-xs font-medium text-gray-500 dark:text-gray-400">Type</p>
                    <p class="text-sm text-gray-900 dark:text-white">{{ incident.type?.name || 'N/A' }}</p>
                </div>
                <div>
                    <p class="text-xs font-medium text-gray-500 dark:text-gray-400">Location</p>
                    <p class="text-sm text-gray-900 dark:text-white">{{ incident.location?.name || 'N/A' }}</p>
                </div>
                <div>
                    <p class="text-xs font-medium text-gray-500 dark:text-gray-400">Reported By</p>
                    <p class="text-sm text-gray-900 dark:text-white">
                        <span v-if="incident.is_anonymous">Anonymous</span>
                        <span v-else>{{ incident.reported_by?.full_name || 'N/A' }}</span>
                    </p>
                </div>
                <div>
                    <p class="text-xs font-medium text-gray-500 dark:text-gray-400">Reported At</p>
                    <p class="text-sm text-gray-900 dark:text-white">{{ formatDate(incident.reported_at) }}</p>
                </div>
            </div>

            <!-- SLA Warning -->
            <div v-if="incident.type?.sla_response_time_hours" class="mt-4 p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg">
                <div class="flex items-start">
                    <svg class="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    <div class="flex-1">
                        <p class="text-sm font-medium text-blue-900 dark:text-blue-300">SLA Response Required</p>
                        <p class="text-sm text-blue-800 dark:text-blue-400">
                            This incident type requires a response within {{ incident.type.sla_response_time_hours }} hours
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <!-- Decision Path Selection -->
        <div class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow p-6 mb-6">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Select Triage Decision</h3>
            <p class="text-sm text-gray-600 dark:text-gray-400 mb-6">
                Choose the appropriate response path based on your assessment of the incident
            </p>

            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <!-- No Action Button -->
                <button
                    type="button"
                    @click="selectDecision('no_action')"
                    :class="[
                        'relative p-6 border-2 rounded-lg text-left transition-all',
                        selectedDecision === 'no_action'
                            ? 'border-gray-500 bg-gray-50'
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                    ]"
                >
                    <div class="flex items-center justify-between mb-3">
                        <div class="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                            <svg class="w-6 h-6 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                            </svg>
                        </div>
                        <div v-if="selectedDecision === 'no_action'" class="w-5 h-5 bg-gray-500 rounded-full flex items-center justify-center">
                            <svg class="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                            </svg>
                        </div>
                    </div>
                    <h4 class="text-base font-semibold text-gray-900 dark:text-white mb-2">No Action</h4>
                    <p class="text-sm text-gray-600 dark:text-gray-400">
                        Close incident. No further action needed.
                    </p>
                </button>

                <!-- Quick Fix Button -->
                <button
                    type="button"
                    @click="selectDecision('quick_fix')"
                    :class="[
                        'relative p-6 border-2 rounded-lg text-left transition-all',
                        selectedDecision === 'quick_fix'
                            ? 'border-yellow-500 bg-yellow-50'
                            : 'border-gray-200 dark:border-gray-700 hover:border-yellow-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    ]"
                >
                    <div class="flex items-center justify-between mb-3">
                        <div class="w-10 h-10 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center">
                            <svg class="w-6 h-6 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                            </svg>
                        </div>
                        <div v-if="selectedDecision === 'quick_fix'" class="w-5 h-5 bg-yellow-500 rounded-full flex items-center justify-center">
                            <svg class="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                            </svg>
                        </div>
                    </div>
                    <h4 class="text-base font-semibold text-gray-900 dark:text-white mb-2">Quick Fix</h4>
                    <p class="text-sm text-gray-600 dark:text-gray-400">
                        Assign simple corrective action to team member.
                    </p>
                </button>

                <!-- Start Investigation Button -->
                <button
                    type="button"
                    @click="selectDecision('investigation')"
                    :class="[
                        'relative p-6 border-2 rounded-lg text-left transition-all',
                        selectedDecision === 'investigation'
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-gray-200 dark:border-gray-700 hover:border-purple-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    ]"
                >
                    <div class="flex items-center justify-between mb-3">
                        <div class="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center">
                            <svg class="w-6 h-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                            </svg>
                        </div>
                        <div v-if="selectedDecision === 'investigation'" class="w-5 h-5 bg-purple-500 rounded-full flex items-center justify-center">
                            <svg class="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                            </svg>
                        </div>
                    </div>
                    <h4 class="text-base font-semibold text-gray-900 dark:text-white mb-2">Investigation</h4>
                    <p class="text-sm text-gray-600 dark:text-gray-400">
                        Requires full investigation to determine root cause.
                    </p>
                </button>
            </div>
        </div>

        <!-- Decision Forms -->
        <form @submit.prevent="submitTriage" v-if="selectedDecision">
            <!-- Severity Assessment (Common for all decisions) -->
            <div class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow p-6 mb-6">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Severity & Priority Assessment</h3>
                <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">Based on your review, assess the severity and priority level of this incident:</p>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Severity Level <span class="text-red-500 dark:text-red-400">*</span>
                        </label>
                        <select
                            v-model="form.severity"
                            class="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            :class="{ 'border-red-500 dark:border-red-400': form.errors.severity }"
                            required
                        >
                            <option value="">Select Severity</option>
                            <option value="low">Low - Minor issue with minimal impact</option>
                            <option value="medium">Medium - Moderate impact, requires attention</option>
                            <option value="high">High - Significant impact, requires urgent attention</option>
                            <option value="critical">Critical - Severe impact, immediate action required</option>
                        </select>
                        <p v-if="form.errors.severity" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.severity }}</p>
                    </div>

                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Priority Level <span class="text-red-500 dark:text-red-400">*</span>
                        </label>
                        <select
                            v-model="form.priority"
                            class="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            :class="{ 'border-red-500 dark:border-red-400': form.errors.priority }"
                            required
                        >
                            <option value="">Select Priority</option>
                            <option value="low">Low - Can be scheduled with normal workflow</option>
                            <option value="normal">Normal - Standard response time</option>
                            <option value="high">High - Should be addressed promptly</option>
                            <option value="urgent">Urgent - Requires immediate attention</option>
                        </select>
                        <p v-if="form.errors.priority" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.priority }}</p>
                    </div>
                </div>

                <div class="mt-4">
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Manager Assessment <span class="text-red-500 dark:text-red-400">*</span>
                    </label>
                    <textarea
                        v-model="form.manager_assessment"
                        rows="3"
                        class="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        :class="{ 'border-red-500 dark:border-red-400': form.errors.manager_assessment }"
                        placeholder="Your assessment of the incident and reasoning for the selected response path..."
                        required
                    ></textarea>
                    <p v-if="form.errors.manager_assessment" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.manager_assessment }}</p>
                </div>
            </div>

            <!-- No Action Form -->
            <div v-if="selectedDecision === 'no_action'" class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow p-6 mb-6">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Closure Details</h3>

                <div class="space-y-4">
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Closure Reason <span class="text-red-500 dark:text-red-400">*</span>
                        </label>
                        <select
                            v-model="form.closure_reason"
                            class="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-gray-500 focus:ring-gray-500"
                            :class="{ 'border-red-500 dark:border-red-400': form.errors.closure_reason }"
                            required
                        >
                            <option value="">Select Reason</option>
                            <option value="duplicate">Duplicate Report</option>
                            <option value="no_action_required">No Action Required</option>
                            <option value="insufficient_info">Insufficient Information</option>
                            <option value="out_of_scope">Out of Scope</option>
                            <option value="resolved_by_reporter">Already Resolved by Reporter</option>
                        </select>
                        <p v-if="form.errors.closure_reason" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.closure_reason }}</p>
                    </div>

                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Closure Notes <span class="text-red-500 dark:text-red-400">*</span>
                        </label>
                        <textarea
                            v-model="form.closure_notes"
                            rows="4"
                            class="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-gray-500 focus:ring-gray-500"
                            :class="{ 'border-red-500 dark:border-red-400': form.errors.closure_notes }"
                            placeholder="Explain why this incident is being closed without action..."
                            required
                        ></textarea>
                        <p v-if="form.errors.closure_notes" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.closure_notes }}</p>
                    </div>
                </div>
            </div>

            <!-- Quick Fix Form -->
            <div v-if="selectedDecision === 'quick_fix'" class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow p-6 mb-6">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Quick Fix Details</h3>

                <div class="space-y-4">
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Action Title <span class="text-red-500 dark:text-red-400">*</span>
                        </label>
                        <input
                            v-model="form.quick_fix_title"
                            type="text"
                            class="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                            :class="{ 'border-red-500 dark:border-red-400': form.errors.quick_fix_title }"
                            placeholder="Brief title for the corrective action..."
                            required
                        />
                        <p v-if="form.errors.quick_fix_title" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.quick_fix_title }}</p>
                    </div>

                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Action Description <span class="text-red-500 dark:text-red-400">*</span>
                        </label>
                        <textarea
                            v-model="form.quick_fix_description"
                            rows="4"
                            class="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                            :class="{ 'border-red-500 dark:border-red-400': form.errors.quick_fix_description }"
                            placeholder="Describe the corrective action to be taken..."
                            required
                        ></textarea>
                        <p v-if="form.errors.quick_fix_description" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.quick_fix_description }}</p>
                    </div>

                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Assign To <span class="text-red-500 dark:text-red-400">*</span>
                        </label>
                        <select
                            v-model="form.quick_fix_assigned_to"
                            class="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                            :class="{ 'border-red-500 dark:border-red-400': form.errors.quick_fix_assigned_to }"
                            required
                        >
                            <option value="">Select Team Member</option>
                            <option v-for="user in users" :key="user.id" :value="user.id">
                                {{ user.name }} ({{ user.email }})
                            </option>
                        </select>
                        <p v-if="form.errors.quick_fix_assigned_to" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.quick_fix_assigned_to }}</p>
                    </div>

                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Due Date <span class="text-red-500 dark:text-red-400">*</span>
                        </label>
                        <input
                            v-model="form.quick_fix_due_date"
                            type="date"
                            class="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                            :class="{ 'border-red-500 dark:border-red-400': form.errors.quick_fix_due_date }"
                            required
                        />
                        <p v-if="form.errors.quick_fix_due_date" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.quick_fix_due_date }}</p>
                    </div>
                </div>
            </div>

            <!-- Investigation Form -->
            <div v-if="selectedDecision === 'investigation'" class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow p-6 mb-6">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Investigation Setup</h3>

                <div class="space-y-6">
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Investigation Description <span class="text-red-500 dark:text-red-400">*</span>
                        </label>
                        <textarea
                            v-model="form.investigation_action_description"
                            rows="4"
                            class="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-purple-500 focus:ring-purple-500"
                            :class="{ 'border-red-500 dark:border-red-400': form.errors.investigation_action_description }"
                            placeholder="Describe what needs to be investigated and what the investigator should focus on..."
                            required
                        ></textarea>
                        <p v-if="form.errors.investigation_action_description" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.investigation_action_description }}</p>
                    </div>

                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Assign Investigation To <span class="text-red-500 dark:text-red-400">*</span>
                        </label>
                        <select
                            v-model="form.investigation_assigned_to"
                            class="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-purple-500 focus:ring-purple-500"
                            :class="{ 'border-red-500 dark:border-red-400': form.errors.investigation_assigned_to }"
                            required
                        >
                            <option value="">Select Team Member</option>
                            <option v-for="user in users" :key="user.id" :value="user.id">
                                {{ user.name }} ({{ user.email }})
                            </option>
                        </select>
                        <p v-if="form.errors.investigation_assigned_to" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.investigation_assigned_to }}</p>
                    </div>

                    <div v-if="incident.type?.sla_investigation_time_hours" class="p-4 bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-700 rounded-lg">
                        <div class="flex items-start">
                            <svg class="w-5 h-5 text-purple-600 dark:text-purple-400 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                            </svg>
                            <div class="flex-1">
                                <p class="text-sm font-medium text-purple-900 dark:text-purple-300">Investigation SLA</p>
                                <p class="text-sm text-purple-800 dark:text-purple-400">
                                    Investigation must be completed within {{ incident.type.sla_investigation_time_hours }} hours from now
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Action Buttons -->
            <div class="flex items-center justify-between bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow p-6">
                <Link
                    :href="route('incidents.show', incident.id)"
                    class="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                    Cancel
                </Link>
                <button
                    type="submit"
                    :disabled="form.processing"
                    :class="[
                        'px-6 py-2 border border-transparent rounded-md text-sm font-medium text-white',
                        selectedDecision === 'no_action' ? 'bg-gray-600 hover:bg-gray-700' :
                        selectedDecision === 'quick_fix' ? 'bg-yellow-600 hover:bg-yellow-700' :
                        'bg-purple-600 hover:bg-purple-700',
                        'disabled:opacity-50 disabled:cursor-not-allowed flex items-center'
                    ]"
                >
                    <svg v-if="form.processing" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {{ form.processing ? 'Processing...' : getSubmitButtonText() }}
                </button>
            </div>
        </form>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { Head, Link } from '@inertiajs/vue3';
import { useSecureForm } from '@/Composables/useSecureForm';
import route from '@/Utils/route';
import InfoButton from '@/Components/Common/InfoButton.vue';

const props = defineProps({
    incident: {
        type: Object,
        required: true,
    },
    users: {
        type: Array,
        required: true,
    },
});

const selectedDecision = ref(null);

const form = useSecureForm({
    decision: null,
    severity: props.incident.severity || 'medium',
    priority: props.incident.priority || 'normal',
    manager_assessment: '',

    // For no_action
    closure_reason: '',
    closure_notes: '',

    // For quick_fix
    quick_fix_title: '',
    quick_fix_description: '',
    quick_fix_assigned_to: '',
    quick_fix_due_date: '',

    // For investigation
    investigation_action_description: '',
    investigation_assigned_to: '',
});

const selectDecision = (decision) => {
    selectedDecision.value = decision;
    form.decision = decision;

    // Reset form fields
    form.closure_reason = '';
    form.closure_notes = '';
    form.quick_fix_title = '';
    form.quick_fix_description = '';
    form.quick_fix_assigned_to = '';
    form.quick_fix_due_date = '';
    form.investigation_action_description = '';
    form.investigation_assigned_to = '';
};

const getSubmitButtonText = () => {
    switch (selectedDecision.value) {
        case 'no_action':
            return 'Close Incident';
        case 'quick_fix':
            return 'Assign Quick Fix';
        case 'investigation':
            return 'Start Investigation';
        default:
            return 'Submit';
    }
};

const submitTriage = () => {
    form.post(route('incidents.triage.store', props.incident.id), {
        preserveScroll: true,
        onSuccess: () => {
            // Redirect handled by controller
        },
    });
};

const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};
</script>
